/**
 * Reading a character without assuming which game it came from.
 *
 * `Character` used to require a class, a proficiency bonus, an armour class and
 * hit dice, so every caller could read them directly and roughly two hundred
 * did. Now that a classless four-ability character can exist, `char.classes[0]`
 * and `char.classes.reduce(...)` are crashes waiting for the first Trophy Dark
 * table, and `+char.proficiencyBonus` is a NaN in the middle of a stat block.
 *
 * These accessors are where absence is handled once, so no call site has to
 * decide what a character with no class is a level of.
 *
 * ON THE FALLBACKS: each one below answers a different question, and they are
 * deliberately not all the same. A missing class list means "this system has no
 * classes", so the total level is 0 and the display is empty — there is no
 * level 1 to fall back to. A missing proficiency bonus means "this system does
 * not scale a flat number with level", so it contributes 0 to a roll. A missing
 * armour class has no honest number at all, so it returns undefined and the
 * caller decides whether to render a dash or hide the box. Inventing a 10 there
 * would put a defence rating on a sheet whose system has no defence rating.
 */
// =============================================================================
// LEVEL AND CLASS
// =============================================================================
/** A character's classes, never undefined, empty for a classless system. */
export function classesOf(char) {
    return char.classes ?? [];
}
/**
 * Total level across all classes, or 0 for a system that has no classes.
 *
 * Zero rather than one: a Trophy character is not a level 1 anything, and a
 * caller that wants "at least 1" for a maths reason should say so at its own
 * site rather than have this lie on its behalf.
 */
export function totalLevel(char) {
    return classesOf(char).reduce((sum, c) => sum + c.level, 0);
}
/**
 * "Fighter (Champion) 5 / Wizard 2", or an empty string for a classless
 * character.
 *
 * Empty, not "Adventurer": this string lands next to a character's name, and a
 * system that deliberately has no classes should show nothing there rather than
 * a word Machina made up.
 */
export function classDisplay(char) {
    return classesOf(char)
        .map((c) => `${c.name}${c.subclass ? ` (${c.subclass})` : ''} ${c.level}`)
        .join(' / ');
}
/** The primary class name, for icons and one-line summaries. Undefined when classless. */
export function primaryClassName(char) {
    return classesOf(char)[0]?.name;
}
/** Whether this character's system gives it classes at all. */
export function hasClasses(char) {
    return classesOf(char).length > 0;
}
// =============================================================================
// 5e-FAMILY NUMBERS
// =============================================================================
/**
 * Proficiency bonus, or 0 for a system without one.
 *
 * Zero is the right identity here because this number is only ever added to a
 * roll. A system with no proficiency bonus contributes nothing to the total,
 * which is exactly what "no proficiency bonus" means.
 */
export function proficiencyBonusOf(char) {
    return char.proficiencyBonus ?? 0;
}
/**
 * Armour class, or undefined when the system has no such stat.
 *
 * Undefined rather than 10, because 10 is a real armour class and a system
 * without armour classes does not have one. Callers rendering a stat box should
 * hide it or show a dash; callers doing 5e combat maths against a 5e character
 * can `?? 10` at their own site, where that default is defensible.
 */
export function armorClassOf(char) {
    return char.armorClass;
}
/** Hit dice as written ("3d10"), or undefined for a system without them. */
export function hitDiceOf(char) {
    return char.hitDice;
}
/** Unspent hit dice, or 0 for a system without them. */
export function hitDiceRemainingOf(char) {
    return char.hitDiceRemaining ?? 0;
}
// =============================================================================
// ABILITIES
// =============================================================================
/**
 * One ability score by storage key.
 *
 * `abilityScores` is a record now, so the compiler will hand you a `number` for
 * an ability the character does not have. Use this anywhere the character might
 * genuinely be from another system; keep reading the field directly in code
 * that already knows it is looking at a 5e character.
 *
 * The fallback defaults to 10 — the d20 score that means "no modifier" — so a
 * 5e calculation reached by a character missing a score degrades to a flat roll
 * rather than to NaN.
 */
export function abilityScore(scores, key, fallback = 10) {
    const score = scores?.[key];
    return typeof score === 'number' ? score : fallback;
}
/** Whether the character actually carries this ability. */
export function hasAbility(scores, key) {
    return typeof scores?.[key] === 'number';
}
//# sourceMappingURL=character-access.js.map